import { useMemo } from "react";

import { useSourcingSettings } from "@components/SourcingThemes/hooks/useSourcingSettings";
import {
  ITagResponse,
  IDealTagResponse,
  IMeetingTagResponse,
  ICompanyTagResponse,
  IPeopleTagResponse,
  INoteTagResponse,
  IMemberTagResponse,
  IDocumentTagResponse,
  IThemeTagResponse,
  IThreadTagResponse,
  IFundTagResponse,
  IOpportunityTagResponse,
} from "@interfaces/responses.interface";
import { DealTag } from "./DealTag";
import { MeetingTag } from "./MeetingTag";
import { CompanyTag } from "./CompanyTag";
import { PeopleTag } from "./PeopleTag";
import { NoteTag } from "./NoteTag";
import { MemberTag } from "./MemberTag";
import { DocumentTag } from "./DocumentTag";
import { ThemeTag } from "./ThemeTag";
import { ThreadTag } from "./ThreadTag";
import { FundTag } from "./FundTag";
import { OpportunityTag } from "./OpportunityTag";

type TagProps = {
  data: ITagResponse;
  isDisabled?: boolean;
  onAdd?: (tag: ITagResponse) => void;
  onRemove?: (tag: ITagResponse) => void;
  withPlusIcon?: boolean;
  iconOnly?: boolean;
};

export const Tag = ({
  data,
  isDisabled = false,
  onAdd,
  onRemove,
  withPlusIcon,
  iconOnly,
}: TagProps) => {
  const { data: sourcingSettings } = useSourcingSettings();

  const hasSingleZeroLevelTheme = sourcingSettings?.zero_level_count === 1;

  const renderTagByType = useMemo(() => {
    switch (data?.object_type) {
      case "deals.deal":
        return (
          <DealTag
            data={data as IDealTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "meetings.meeting":
        return (
          <MeetingTag
            data={data as IMeetingTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "companies.company":
        return (
          <CompanyTag
            data={data as ICompanyTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "people.person":
        return (
          <PeopleTag
            data={data as IPeopleTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withRedirect
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "notes.ainote":
        return (
          <NoteTag
            data={data as INoteTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "accounts.user":
        return (
          <MemberTag
            data={data as IMemberTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "documents.documentfile":
        return (
          <DocumentTag
            data={data as IDocumentTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "documents.folder":
        return (
          <DocumentTag
            data={data as IDocumentTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "sourcing.theme":
        return (
          <ThemeTag
            data={data as IThemeTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
            isLevelOneBased={hasSingleZeroLevelTheme}
          />
        );
      case "emails.thread":
        return (
          <ThreadTag
            data={data as IThreadTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "fundraising.fund":
        return (
          <FundTag
            data={data as IFundTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      case "fundraising.opportunity":
        return (
          <OpportunityTag
            data={data as IOpportunityTagResponse}
            isDisabled={isDisabled}
            onRemove={onRemove}
            withPlusIcon={withPlusIcon}
            iconOnly={iconOnly}
            onAdd={onAdd}
          />
        );
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isDisabled, onRemove]);

  return <>{renderTagByType}</>;
};
